import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthenticationWrapper";
import { HistoryList } from "../../../../components/HistoryList";
import CustomButton from "../../../../components/lib/customButton";
import { formatCurrency } from "../../../../utils/CurrencyUtils";
import CustomerService from "../../../../rest/customerService";
import OrderService from "../../../../rest/orderService";
import OrderInfo from "../../../../components/orderInfo";
import { formatString } from "../../../../utils/StringUtils";
import InfoField from "../../../../components/lib/infoField";
import ConfirmDeleteModal from "../../../../components/reusables/confirmDeleteModal";
import { toast } from "react-toastify";
import NoPictureWarning from "../../../../resources/nopic.png";
import CreditRechargeModal from "../../../../components/reusables/creditRechargeModal";
import StudentEditStepper from "./studentEditStepper";
import ExpenseModal from "../../../../components/reusables/expenseModal";
import Modal from "../../../../components/lib/modal";
import ConfirmCloseView from "../confirmCloseView";
import { useStudentLiveUpdate } from "../../hooks/student/useStudentLiveUpdate";
import { Transition } from "@headlessui/react";
import SmartImage from "../../../../components/reusables/smartImage";
import ClientService from "../../../../rest/clientService";

const StudentDetailsModalContent = ({ client, onEdit, onDelete }) => {
    const { hasPermission } = useAuth();
    const liveClient = useStudentLiveUpdate(client);
    
    const canEdit = hasPermission("customers", "update");
    const canUpdateCredits = hasPermission("customers", "update_credits");
    const canAddExpenses = hasPermission("customers", "add_expense");
    const canDelete = hasPermission("customers", "delete");

    const [parenting, setParenting] = useState([]);
    const [orders, setOrders] = useState([]);
    const [allTurns, setAllTurns] = useState([]);
    const [preferredTurnIds, setPreferredTurnIds] = useState([]);
    const [order, setOrder] = useState(null);
    const [showOrderInfo, setShowOrderInfo] = useState(false);    
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showRechargeModal, setShowRechargeModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditSubmitting, setIsEditSubmitting] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) setConfirmClose(true);
        else setShowEditModal(false);
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!liveClient?.login) return;
            try {
                const [parentResult, orderResult, rechargeResult, expensesResult, turnsResult, prefsResult] = await Promise.allSettled([
                    CustomerService.findParentsByStudent(liveClient.id_student),
                    OrderService.getAllOrdersFromClient(liveClient.login),
                    OrderService.getAllCreditRechargesFromClient(liveClient.login),
                    OrderService.getAllExpensesForClient(liveClient.login),
                    ClientService.getTurns(),
                    CustomerService.getStudentTurnPreferences(liveClient.login),
                ]);

                if (parentResult.status === "fulfilled") setParenting(parentResult.value);
                if (turnsResult.status === "fulfilled") setAllTurns(turnsResult.value || []);
                if (prefsResult.status === "fulfilled") setPreferredTurnIds(prefsResult.value || []);

                const safeOrders = orderResult.status === "fulfilled" && Array.isArray(orderResult.value) ? orderResult.value : [];
                const safeRecharges = rechargeResult.status === "fulfilled" && Array.isArray(rechargeResult.value) ? rechargeResult.value : [];
                const safeExpenses = expensesResult.status === "fulfilled" && Array.isArray(expensesResult.value) ? expensesResult.value : [];

                setOrders([...safeOrders, ...safeRecharges, ...safeExpenses]);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [liveClient.login, liveClient.credit]);

    const handleOrderClick = (selectedOrder) => {
        setOrder(selectedOrder);
        setShowOrderInfo(true);
    };

    const handleDelete = async () => {
        try {
            await CustomerService.deleteStudent(liveClient.login);
            toast.success("Aluno removido!");
            setShowModalDelete(false);
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover.");
        }
    };

    const handleEditFinish = async ({ student, turnIds }) => {
        if (isEditSubmitting) return;
        setIsEditSubmitting(true);
        try {
            await CustomerService.updateStudent(student.login, student);
            await CustomerService.updateStudentTurnPreferences(student.login, turnIds);
            setPreferredTurnIds(turnIds);
            toast.success('Aluno atualizado!');
            setShowEditModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar');
        } finally {
            setIsEditSubmitting(false);
        }
    };

    const resolveStatusBadge = (order) => {
        if (order.transaction_type === 'Credit Recharge Asaas' && order.asaasStatus) {
            const map = { CONFIRMED: ['Confirmado', 'bg-green-100 text-green-700'], FAILED: ['Falhou', 'bg-red-100 text-red-700'], PENDING: ['Pendente', 'bg-yellow-100 text-yellow-700'] };
            const [label, cls] = map[order.asaasStatus] || ['Pendente', 'bg-yellow-100 text-yellow-700'];
            return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase w-fit ${cls}`}>{label}</span>;
        }
        if (order.status) {
            const map = {
                FINISHED:          ['Finalizado',              'bg-green-100 text-green-700'],
                COMPLETED:         ['Pago',                    'bg-blue-100 text-blue-700'],
                READYTODELIVER:    ['Pronto p/ Entrega',       'bg-purple-100 text-purple-700'],
                PROCESSINGPAYMENT: ['Aguard. Pagamento',       'bg-yellow-100 text-yellow-700'],
                CANCELED:          ['Cancelado',               'bg-red-100 text-red-700'],
            };
            const [label, cls] = map[order.status] || [null, null];
            if (label) return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase w-fit ${cls}`}>{label}</span>;
        }
        return null;
    };

    const renderOrderItem = (order) => (
        <div className="flex justify-between items-center w-full text-xs sm:text-sm py-1">
            <div className="flex flex-col overflow-hidden mr-2">
                <span className={`font-semibold truncate ${order.status === 'CANCELED' ? 'text-custom_red line-through' : 'text-gray-700'}`}>
                    {order.items_list
                        ? formatString(order.items_list)
                        : order.transaction_type === 'Credit Recharge Admin' ? "Recarga Manual" : order.transaction_type === 'Credit Recharge Asaas' ? "Recarga de Saldo" : "Correção"}
                </span>
                {resolveStatusBadge(order)}
            </div>
            <span className={`font-bold whitespace-nowrap ${order.status === 'CANCELED' ? 'text-custom_red' : 'text-gray-800'}`}>
                {order.transaction_type === 'Credit Expense' ? "-" : ""}
                {order.total ? formatCurrency(order.total) : formatCurrency(order.value)}
            </span>
        </div>
    );

    const profileImageUrl = `/foodz/api/v1/uploads/carregarImagemDePerfil?path=${encodeURIComponent(liveClient.image_url)}`;

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            <div className="w-full aspect-[4/5] sm:aspect-video lg:aspect-[4/5] bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 shadow-md relative group">
                                
                                {liveClient.image_url ? (
                                    <SmartImage
                                        src={profileImageUrl}
                                        alt="Profile"
                                        isThumb={false}
                                        type="profile"
                                        className="object-cover group-hover:scale-105"
                                    />
                                ) : (
                                    <img
                                        src={NoPictureWarning}
                                        alt="Profile"
                                        className="absolute inset-0 w-full h-full opacity-40 object-contain p-8"
                                    />
                                )}

                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100" />

                                <div className={`absolute top-3 left-3 z-20 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 ${liveClient.user_status ? 'bg-green-500' : 'bg-red-500'}`}>
                                    {liveClient.user_status ? 'ATIVO' : 'INATIVO'}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <InfoField label="Nome Completo" value={liveClient.name} />
                                <div className="grid grid-cols-2 gap-3">
                                    <InfoField label="Série/Turma" value={liveClient.grade} />
                                    <InfoField label="Pagante?" value={liveClient.isPayingStudent ? "Sim" : "Não"} />
                                </div>

                                {allTurns.length > 0 && (
                                    <div className="mb-3 w-full min-w-0">
                                        <p className="text-sm font-semibold text-custom_gray_font mb-1">Turnos preferidos</p>
                                        {preferredTurnIds.length === 0 ? (
                                            <p className="px-3 py-1.5 border rounded text-sm text-gray-400 italic">Qualquer turno</p>
                                        ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                                {allTurns
                                                    .filter(t => preferredTurnIds.includes(t.turnId))
                                                    .map(t => (
                                                        <span key={t.turnId} className="inline-flex flex-col text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-full leading-tight">
                                                            <span>{t.name}</span>
                                                            <span className="text-[10px] text-blue-400 font-normal">{t.startTime || t.start} – {t.endTime || t.end}</span>
                                                        </span>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-3">
                                    <InfoField label="Login (Matrícula)" value={liveClient.login} />
                                    <InfoField label="Email" value={liveClient.email || "-"} />
                                </div>
                            </div>
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className={`border p-4 rounded-xl flex flex-col justify-between ${liveClient.credit >= 0 ? "bg-green-50 border-green-100 " : "bg-red-50 border-red-100 "}`}>
                                    <span className={`text-xs font-bold uppercase ${liveClient.credit >= 0 ? "text-green-700" : "text-red-500"}`}>Saldo Atual</span>
                                    <span className={`text-2xl font-bold ${liveClient.credit >= 0 ? "text-green-600" : "text-red-500"}`}>{formatCurrency(liveClient.credit)}</span>
                                </div>
                                <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex flex-col justify-between">
                                    <span className="text-xs text-blue-700 font-bold uppercase">Emergência Disp.</span>
                                    <span className="text-xl font-bold text-blue-600">
                                        {liveClient.credit <= 0 ? formatCurrency(liveClient.emergency_value + liveClient.credit) : formatCurrency(liveClient.emergency_value)}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-center bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <div>
                                    <p className="text-[10px] uppercase text-gray-500 font-bold">Limite Emergência</p>
                                    <p className="font-semibold text-gray-700">{formatCurrency(liveClient.emergency_value || 0)}</p>
                                </div>
                                <div className="border-l border-gray-300">
                                    <p className="text-[10px] uppercase text-gray-500 font-bold">Limite Diário</p>
                                    <p className="font-semibold text-gray-700">{formatCurrency(liveClient.spend_limit || 0)}</p>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                <p className="text-xs font-bold text-gray-500 uppercase mb-2 tracking-wide">Responsáveis</p>
                                {parenting.length > 0 ? (
                                    <div className="flex flex-col gap-2">
                                        {parenting.map((p, i) => (
                                            <div key={i} className="text-sm font-medium text-gray-700 bg-white border border-gray-200 px-3 py-2 rounded shadow-sm">
                                                {p.name}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">Nenhum vinculado</p>
                                )}
                            </div>

                            <div className="flex-1 min-h-[180px] flex flex-col border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="flex-1 overflow-y-auto max-h-100 bg-white p-2">
                                    <HistoryList data={orders} renderItem={renderOrderItem} onItemClick={handleOrderClick} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {order && (
                        <OrderInfo
                            order={order}
                            showOrderInfoModal={showOrderInfo}
                            handleCancelClick={() => setShowOrderInfo(false)}
                            customer={liveClient}
                        />
                    )}
                </div>

                <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg">
                    <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3">
                        <CustomButton buttonType="editCredits" text="Recarregar" onClick={() => setShowRechargeModal(true)} disabled={!canUpdateCredits} className="w-full sm:w-auto text-sm" />
                        <CustomButton buttonType="cancel" text="Corrigir" onClick={() => setShowExpenseModal(true)} disabled={!canAddExpenses} className="w-full sm:w-auto text-sm" />
                        <div className="hidden sm:block w-px bg-gray-300 mx-2"></div>
                        <CustomButton buttonType="edit" text="Editar" onClick={() => setShowEditModal(true)} disabled={!canEdit} className="w-full sm:w-auto text-sm" />
                        <CustomButton buttonType="delete" text="Excluir" onClick={() => setShowModalDelete(true)} disabled={!canDelete} className="w-full sm:w-auto text-sm" />
                    </div>
                </div>

                <CreditRechargeModal isOpen={showRechargeModal} onClose={() => setShowRechargeModal(false)} client={liveClient} parents={parenting} />
                <ExpenseModal isOpen={showExpenseModal} onClose={() => setShowExpenseModal(false)} client={liveClient} />

                {showEditModal && (
                    <Modal isOpen={showEditModal} setIsOpen={requestClose} title="Editar Aluno" hideFooter maxWidth="max-w-xl lg:max-w-2xl md:-mr-16">
                        <div className="relative h-full flex flex-col">
                            <StudentEditStepper
                                student={liveClient}
                                initialTurnIds={preferredTurnIds}
                                onFinish={handleEditFinish}
                                onClose={requestClose}
                                onDirty={() => setHasData(true)}
                                isSubmitting={isEditSubmitting}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView onCancel={() => setConfirmClose(false)} onConfirm={forceClose} />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir"
                    message={`Deseja excluir ${liveClient.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default StudentDetailsModalContent;